import { deepMerge, sleepAsync } from '@sensenet/client-utils';
import { useRepository } from '@sensenet/hooks-react';
import React, { createContext, useCallback, useEffect, useState } from 'react';
import Semaphore from 'semaphore-async-await';
import { POLLING_INTERVAL } from "../components/index.js";
import { PreviewState } from "../enums.js";
import { useDocumentViewerApi, useViewerSettings } from "../hooks/index.js";
const defaultDocumentData = {
  documentName: '',
  documentType: '',
  fileSizekB: 0,
  hostName: '',
  idOrPath: 0,
  pageCount: PreviewState.Loading,
  shapes: {
    annotations: [],
    highlights: [],
    redactions: []
  },
  error: undefined
};
export const defaultDocumentDataContextValue = {
  documentData: defaultDocumentData,
  updateDocumentData: () => undefined,
  isInProgress: false,
  triggerReload: () => {}
};
export const DocumentDataContext = /*#__PURE__*/createContext(defaultDocumentDataContextValue);
export const DocumentDataProvider = _ref => {
  let {
    children
  } = _ref;
  const api = useDocumentViewerApi();
  const doc = useViewerSettings();
  const repo = useRepository();
  const [documentData, setDocumentData] = useState(defaultDocumentData);
  const [loadLock] = useState(new Semaphore(1));
  const [isInProgress, setIsInProgress] = useState(false);
  const [reloadToken, setReloadToken] = useState(0);

  const triggerReload = () => setReloadToken(prevValue => prevValue + 1);

  useEffect(() => {
    const ac = new AbortController();

    const getData = async () => {
      const result = await api.getDocumentData({
        hostName: repo.configuration.repositoryUrl,
        idOrPath: doc.documentIdOrPath,
        version: doc.version,
        abortController: ac
      });

      if (result.pageCount === PreviewState.Loading && ac.signal.aborted === false) {
        setDocumentData(result);
        await sleepAsync(POLLING_INTERVAL);
        getData();
      } else {
        setDocumentData(result);
      }
    };

    (async () => {
      try {
        setIsInProgress(true);
        await loadLock.acquire();
        await getData();
      } catch (error) {
        if (!ac.signal.aborted) {
          setDocumentData({ ...defaultDocumentData,
            pageCount: PreviewState.ClientFailure,
            error: error.toString()
          });
          throw error;
        }
      } finally {
        setIsInProgress(false);
        loadLock.release();
      }
    })();

    return () => ac.abort();
  }, [api, doc.documentIdOrPath, doc.version, loadLock, repo.configuration.repositoryUrl, reloadToken]);
  const updateDocumentData = useCallback((newDocData, force) => {
    const merged = deepMerge(documentData, newDocData);
    force ? setDocumentData(merged) : JSON.stringify(documentData) !== JSON.stringify(merged) && setDocumentData(merged);
  }, [documentData]);

  if (!documentData.idOrPath) {
    return null;
  }

  return /*#__PURE__*/React.createElement(DocumentDataContext.Provider, {
    value: {
      documentData,
      updateDocumentData,
      isInProgress,
      triggerReload
    }
  }, children);
};
//# sourceMappingURL=document-data.js.map